import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import type {
  WireChatMessage,
  WireConversation,
  WireConversationSummary,
  QuestionOption,
} from '@logistics/contracts';
import { api } from '../client';
import { expectStatus, failureOf } from '@/lib/result';
import { useToast } from '../shell/toast';

export interface ScopeOption {
  readonly option: QuestionOption;
  readonly label: string;
  readonly available: boolean;
}

export interface ConversationPage {
  readonly items: WireConversationSummary[];
  readonly nextBefore?: string | undefined;
}

export interface ConversationData {
  readonly conversation: WireConversation;
  readonly messages: WireChatMessage[];
}

export const CONVERSATIONS_KEY = ['chat', 'conversations'] as const;
export const conversationKey = (id: string): readonly string[] => ['chat', 'conversation', id];

/** The five sources a question can be asked of, each marked available or not. */
export function useScopes(): UseQueryResult<ScopeOption[]> {
  return useQuery({
    queryKey: ['chat', 'scopes'],
    queryFn: async () => expectStatus(await api.chat.scopes(), 200).scopes,
    staleTime: 60_000,
  });
}

/** The RECENT list, newest activity first, a page at a time. */
export function useConversations(): UseInfiniteQueryResult<InfiniteData<ConversationPage>> {
  return useInfiniteQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: async ({ pageParam }) => {
      const query = { limit: 30, ...(pageParam === undefined ? {} : { before: pageParam }) };
      return expectStatus(await api.chat.conversations({ query }), 200);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextBefore,
  });
}

/**
 * One conversation's messages. Never refetched on its own: while an answer is
 * streaming the server already holds the question, and a background refetch
 * would show it twice. The page invalidates it when a turn ends.
 */
export function useConversation(id: string | null): UseQueryResult<ConversationData> {
  return useQuery({
    queryKey: conversationKey(id ?? ''),
    queryFn: async () =>
      expectStatus(await api.chat.conversation({ params: { id: id ?? '' } }), 200),
    enabled: id !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Forgets a conversation, then refreshes the list. */
export function useDeleteConversation(): UseMutationResult<
  boolean,
  Error,
  WireConversationSummary
> {
  const toast = useToast();
  const client = useQueryClient();
  return useMutation<boolean, Error, WireConversationSummary>({
    mutationFn: async (conversation) => {
      const r = await api.chat.remove({ params: { id: conversation.id } });
      if (r.status !== 204) {
        toast(failureOf(r).detail ?? `Could not delete the conversation (${r.status})`);
        return false;
      }
      toast('Conversation deleted');
      client.removeQueries({ queryKey: conversationKey(conversation.id) });
      await client.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
      return true;
    },
  });
}
